import * as React from "react";
import { ArrowRight, FileSpreadsheet, Loader2, RefreshCw } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  cn,
} from "@eduviaos/ui-kit";
import type {
  StudentImportInvalidRow,
  StudentImportPreviewItem,
  StudentImportPreviewResponse,
  StudentImportTemplateInfoResponse,
} from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";
import { buildDynamicFieldLabelMap } from "@/pages/studentImportDynamicColumns";
import { formatBytes, VERDICT_LABEL, VERDICT_VARIANT } from "./studentImportShared";

/**
 * Phase H6d — shared preview + summary layout, extracted from the
 * wizard's `<PreviewState>` so the standing importer reuses the same
 * row visuals + summary card. The CTA copy ("Import N students") is
 * stable; the parent owns the action callback so the wizard and
 * standing surfaces can wire their own post-commit behaviour without
 * branching on a flag inside the card.
 */
export interface PreviewCardProps {
  file: File | null;
  preview: StudentImportPreviewResponse;
  /** Used to resolve dynamic-column rejection keys to their
   *  institute-authored labels. `null` while the template query is in
   *  flight; the display gracefully falls back to the raw key. */
  template: StudentImportTemplateInfoResponse | null;
  onCommit: () => void;
  onReset: () => void;
  isCommitting: boolean;
  /** Label override for the "Replace file" button — wizard keeps the
   *  default; the standing surface can rename to "Pick another file"
   *  if needed. Kept as a prop so both orchestrators stay readable. */
  replaceLabel?: string;
}

export function PreviewCard({
  file,
  preview,
  template,
  onCommit,
  onReset,
  isCommitting,
  replaceLabel = "Replace file",
}: PreviewCardProps) {
  const { items, invalid, summary } = preview;
  const freshCount = summary.fresh_rows;
  const { ct, terms } = useTerms();
  const fieldLabels = React.useMemo(
    () => buildDynamicFieldLabelMap(template?.dynamic_columns ?? []),
    [template],
  );
  const canonicalLabels = template?.canonical_column_labels ?? {};
  return (
    <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 grid gap-6 motion-safe:duration-500 lg:grid-cols-[1fr_280px]">
      <div className="grid gap-4">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
            <div className="grid gap-1">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSpreadsheet className="size-4" aria-hidden="true" />
                {file?.name ?? "Imported file"}
              </CardTitle>
              <CardDescription>
                {summary.total_rows} row{summary.total_rows === 1 ? "" : "s"} parsed ·{" "}
                {Object.keys(preview.header_map).length} column
                {Object.keys(preview.header_map).length === 1 ? "" : "s"} mapped
                {file ? <> · {formatBytes(file.size)}</> : null}
              </CardDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onReset}
              disabled={isCommitting}
            >
              <RefreshCw className="size-3.5" aria-hidden="true" />
              {replaceLabel}
            </Button>
          </CardHeader>
          <CardContent className="grid gap-2 p-0">
            {items.length === 0 && invalid.length === 0 ? (
              <p className="text-muted-foreground px-6 py-8 text-center text-sm">
                No rows parsed. Make sure your CSV has a header row and at least one data row below
                it.
              </p>
            ) : (
              <div className="border-border divide-border/60 max-h-[420px] divide-y overflow-y-auto border-t">
                {items.map((row) => (
                  <PreviewRow key={`item-${row.line_no}`} row={row} ct={ct} />
                ))}
                {invalid.map((row) => (
                  <InvalidRowDisplay
                    key={`invalid-${row.line_no}-${row.field}`}
                    row={row}
                    fieldLabels={fieldLabels}
                    canonicalLabels={canonicalLabels}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 self-start">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Summary</CardTitle>
            <CardDescription>What we&apos;ll do on import.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            <SummaryRow label="Total rows" value={summary.total_rows} />
            <SummaryRow label="Will be imported" value={summary.fresh_rows} tone="success" />
            <SummaryRow label="Skipped as duplicates" value={summary.duplicate_rows} tone="warn" />
            <SummaryRow label="Invalid rows" value={summary.invalid_rows} tone="danger" />
            <Separator />
            <Button
              type="button"
              size="lg"
              onClick={onCommit}
              disabled={isCommitting || freshCount === 0}
              className="group mt-1 w-full gap-2"
              data-testid="student-import-commit"
            >
              {isCommitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Importing…
                </>
              ) : freshCount === 0 ? (
                "No fresh rows to import"
              ) : (
                <>
                  Import {freshCount}{" "}
                  {(freshCount === 1 ? terms.learner.one : terms.learner.many).toLowerCase()}
                  <ArrowRight
                    className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                </>
              )}
            </Button>
            {summary.invalid_rows > 0 ? (
              <p className="text-muted-foreground text-[11px] leading-relaxed">
                Invalid rows are skipped automatically. Fix them in the file and re-upload to bring
                them in.
              </p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PreviewRow({ row, ct }: { row: StudentImportPreviewItem; ct: (text: string) => string }) {
  return (
    <div className="grid gap-1 px-6 py-3 sm:grid-cols-[40px_1fr_auto] sm:items-center sm:gap-3">
      <span className="text-muted-foreground font-mono text-xs">L{row.line_no}</span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{row.name || "—"}</p>
        {row.email ? <p className="text-muted-foreground truncate text-xs">{row.email}</p> : null}
        {row.notes ? <p className="text-muted-foreground/80 text-[11px]">{row.notes}</p> : null}
      </div>
      <Badge variant={VERDICT_VARIANT[row.verdict]} title={row.notes ?? undefined}>
        {ct(VERDICT_LABEL[row.verdict])}
      </Badge>
    </div>
  );
}

interface InvalidRowDisplayProps {
  row: StudentImportInvalidRow;
  fieldLabels: Record<string, string>;
  canonicalLabels: Record<string, string>;
}

function InvalidRowDisplay({ row, fieldLabels, canonicalLabels }: InvalidRowDisplayProps) {
  const dynamicLabel = fieldLabels[row.field];
  const isDynamic = dynamicLabel !== undefined;
  const friendlyField = dynamicLabel ?? canonicalLabels[row.field] ?? row.field.replace(/_/g, " ");
  return (
    <div
      className="bg-destructive/5 grid gap-1 px-6 py-3 sm:grid-cols-[40px_1fr_auto] sm:items-center sm:gap-3"
      data-field-key={row.field}
      data-field-source={isDynamic ? "dynamic" : "canonical"}
    >
      <span className="text-muted-foreground font-mono text-xs">L{row.line_no}</span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          <span className={cn("text-muted-foreground text-xs", isDynamic && "text-foreground/70")}>
            {friendlyField}:
          </span>{" "}
          {row.message}
        </p>
      </div>
      <Badge variant="danger">Invalid row</Badge>
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  value: number;
  tone?: "success" | "warn" | "danger";
}

function SummaryRow({ label, value, tone }: SummaryRowProps) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "font-mono tabular-nums",
          tone === "success" && "text-emerald-600 dark:text-emerald-400",
          tone === "warn" && "text-amber-600 dark:text-amber-400",
          tone === "danger" && "text-destructive",
        )}
      >
        {value}
      </span>
    </div>
  );
}
